import React, { useState, useEffect, useRef } from "react";
import { PageHeader } from "@finitless/shared/components";
import ReportCard from "../reports/ReportCard";
import MonthYearPicker from "../reports/MonthYearPicker";
import {
  useDownloadExecutiveReport,
  useDownloadPerformanceReport,
} from "../../../hooks/useReports";
import { useToast, usePostHog } from "@finitless/shared/hooks";
import {
  downloadBlob,
  generateReportFilename,
  getCurrentMonth,
} from "../../../lib/reportUtils";
import { useUserStore } from "@finitless/shared/stores";
import { BusinessDropdown } from "../../ui/Business/BusinessDropdown";
import { Icon } from "../../ui/UIElements/Icon";
import { Button } from "../../ui/UIElements/Button";
import { useTranslation } from "react-i18next";
import "../overview-reports.css";

const ReportsPage = () => {
  const { t } = useTranslation(["reports", "toasts"]);
  const [selectedDate, setSelectedDate] = useState(() => {
    const currentMonth = getCurrentMonth();
    const [year, month] = currentMonth.split("-");
    return new Date(parseInt(year), parseInt(month) - 1, 1);
  });
  const toast = useToast();
  const { trackFeatureAdoption } = usePostHog();
  const user = useUserStore((state) => state.user);

  // Local state for business filter (doesn't modify global context)
  const [filterBusiness, setFilterBusiness] = useState(null);
  const [isBusinessDropdownOpen, setIsBusinessDropdownOpen] = useState(false);
  const businessDropdownRef = useRef(null);

  // Close business dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        businessDropdownRef.current &&
        !businessDropdownRef.current.contains(event.target)
      ) {
        setIsBusinessDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleBusinessSelect = (brand) => {
    setFilterBusiness(brand);
    setIsBusinessDropdownOpen(false);
  };

  const handleClearBusinessFilter = (e) => {
    e.stopPropagation();
    setFilterBusiness(null);
  };

  // Initialize the mutation hooks
  const executiveReportMutation = useDownloadExecutiveReport();
  const performanceReportMutation = useDownloadPerformanceReport();

  // Track analytics page view
  useEffect(() => {
    if (user?.id) {
      trackFeatureAdoption.analyticsPageViewed({
        user_id: user.id,
      });
    }
  }, [user?.id, trackFeatureAdoption]);

  // Helper function to convert Date to YYYY-MM format
  const getMonthString = (date) => {
    if (!date) return getCurrentMonth();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;
  };

  // Handle executive report download
  const handleExecutiveReportDownload = async () => {
    try {
      const monthString = getMonthString(selectedDate);
      const params = {
        month: monthString,
      };

      if (filterBusiness) {
        params.business_id = filterBusiness.id;
      }

      const blob = await executiveReportMutation.mutateAsync(params);

      const filename = generateReportFilename("executive", monthString);
      downloadBlob(blob, filename);

      // Track data export
      trackFeatureAdoption.dataExported({
        export_type: "executive_report",
        month: monthString,
        business_id: filterBusiness?.id || null,
        user_id: user?.id,
      });

      toast.success("Executive report downloaded successfully", {
        title: t("reports:toasts.downloadCompleteTitle"),
        description: t("reports:toasts.executiveDownloadSuccess", {
          month: monthString,
        }),
      });
    } catch (error) {
      toast.error(t("reports:toasts.executiveDownloadError"), {
        title: t("reports:toasts.downloadErrorTitle"),
        description: error.message || t("reports:toasts.genericDownloadError"),
      });
    }
  };

  // Handle performance report download
  const handlePerformanceReportDownload = async () => {
    try {
      const monthString = getMonthString(selectedDate);
      const params = {
        month: monthString,
      };

      if (filterBusiness) {
        params.business_id = filterBusiness.id;
      }

      const blob = await performanceReportMutation.mutateAsync(params);

      const filename = generateReportFilename("performance", monthString);
      downloadBlob(blob, filename);

      // Track data export
      trackFeatureAdoption.dataExported({
        export_type: "performance_report",
        month: monthString,
        business_id: filterBusiness?.id || null,
        user_id: user?.id,
      });

      toast.success("Performance report downloaded successfully", {
        title: t("reports:toasts.downloadCompleteTitle"),
        description: t("reports:toasts.performanceDownloadSuccess", {
          month: monthString,
        }),
      });
    } catch (error) {
      toast.error(t("reports:toasts.performanceDownloadError"), {
        title: t("reports:toasts.downloadErrorTitle"),
        description: error.message || t("reports:toasts.genericDownloadError"),
      });
    }
  };

  return (
    <div className="ordering-analytics-page ordering-reports-page space-y-6">
      <PageHeader
        title={t("reports:page.title")}
        subtitle={t("reports:page.subtitle")}
      />

      {/* Month Selection */}
      <div className="ordering-analytics-page__filter-panel border-border bg-background rounded-lg border p-4">
        <div className="flex items-center space-x-4">
          <div className="max-w-xs flex-1">
            <MonthYearPicker
              value={selectedDate}
              onChange={setSelectedDate}
              placeholder={t("reports:monthYearPicker.placeholder")}
              label={t("reports:page.monthPickerLabel")}
              subtitle={t("reports:page.monthPickerSubtitle")}
              id="month-year-picker"
            />
          </div>
        </div>
      </div>

      {/* Business Filter */}
      <div className="ordering-analytics-page__filter-panel border-border bg-background rounded-lg border p-4">
        <div className="flex items-center space-x-4">
          <div className="max-w-xs flex-1">
            <label
              id="reports-business-filter-label"
              className="text-foreground mb-2 block text-sm font-medium"
            >
              {t("reports:page.filters.businessLabel")}
            </label>
            <div className="relative" ref={businessDropdownRef}>
              <Button
                variant="ghost"
                type="button"
                onClick={() =>
                  setIsBusinessDropdownOpen(!isBusinessDropdownOpen)
                }
                aria-labelledby="reports-business-filter-label"
                aria-expanded={isBusinessDropdownOpen}
                aria-controls="reports-business-dropdown"
                className="ordering-analytics-page__control hover:bg-accent border-border bg-card flex h-10 w-full min-w-[180px] items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors"
              >
                <span className="text-foreground truncate">
                  {filterBusiness
                    ? filterBusiness.name
                    : t("reports:page.filters.allBusinesses")}
                </span>
                <Icon
                  name={isBusinessDropdownOpen ? "ChevronUp" : "ChevronDown"}
                  className="text-muted-foreground ml-auto h-4 w-4 flex-shrink-0"
                />
              </Button>
              {filterBusiness && (
                <Button
                  variant="ghost"
                  type="button"
                  onClick={handleClearBusinessFilter}
                  aria-label={t("reports:page.filters.clearBusiness")}
                  className="ordering-analytics-page__clear bg-error hover:bg-error/90 absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full transition-colors"
                  title={t("reports:page.filters.clearBusiness")}
                >
                  <Icon name="X" className="text-foreground h-3 w-3" />
                </Button>
              )}
              <div
                id="reports-business-dropdown"
                className="absolute start-0 top-full z-[var(--layer-dropdown)] mt-2"
              >
                <BusinessDropdown
                  isOpen={isBusinessDropdownOpen}
                  onClose={() => setIsBusinessDropdownOpen(false)}
                  selectedBrand={filterBusiness}
                  onBrandSelect={handleBusinessSelect}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Report Cards Grid */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <ReportCard
          title={t("reports:cards.executive.title")}
          description={t("reports:cards.executive.description")}
          actionText={t("reports:cards.downloadPdf")}
          loadingText={t("reports:cards.generating")}
          onAction={handleExecutiveReportDownload}
          isLoading={executiveReportMutation.isPending}
          icon="TrendingUp"
        />

        <ReportCard
          title={t("reports:cards.performance.title")}
          description={t("reports:cards.performance.description")}
          actionText={t("reports:cards.downloadPdf")}
          loadingText={t("reports:cards.generating")}
          onAction={handlePerformanceReportDownload}
          isLoading={performanceReportMutation.isPending}
          icon="BarChart3"
        />
      </div>

      {/* Additional Information */}
      <div className="ordering-reports-page__info border-border bg-background rounded-lg border p-4">
        <div className="flex items-start space-x-3">
          <div
            className="bg-accent mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full"
            aria-hidden="true"
          >
            <div className="bg-primary h-2 w-2 rounded-full"></div>
          </div>
          <div>
            <h4 className="text-foreground mb-1 text-sm font-medium">
              {t("reports:info.title")}
            </h4>
            <p className="text-muted-foreground text-xs leading-relaxed">
              {t("reports:info.description")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;
